"use client";

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import NextImage from "next/image";
import { motion, useReducedMotion } from "motion/react";
import { stegaClean } from "@sanity/client/stega";
import { cn } from "@/lib/utils";
import { IEliteMentor } from "@/sanity/pages/PageEliteTemplate";
import { CAP_TRIM, DISPLAY, GRADIENT_TRIM_PAD, EliteChip, gradientText } from "./EliteShared";
import { EliteCopy } from "./copy";

/** Time on each confirmed mentor before the carousel moves on (the ring timer's duration). */
const INTERVAL_MS = 3500;

/** Mentor ring in its SVG's 100-unit box (stroke kept in user units so pathLength drives the timer). */
const RING_STROKE = 1.3966;
const RING_R = 50 - RING_STROKE / 2;

/** Portrait/caption morph: the leaving one dissolves into blur while the next one resolves out of it. */
// Explicit properties: tailwindcss-animate makes `duration-[…]`/`delay-[…]`/`ease-[…]` ambiguous in Tailwind 3.3.
const MORPH_EASE = "[transition-timing-function:cubic-bezier(0.65,0,0.35,1)]";
const MORPH_IN = "opacity-100 blur-0 scale-100 translate-y-0 [transition-duration:1100ms]";
const MORPH_OUT =
  "pointer-events-none opacity-0 blur-[1.5rem] scale-[1.04] translate-y-[1.5rem] [transition-duration:750ms]";

/**
 * Portrait framing, measured from Figma and shared by every mentor (the photo is a transparent
 * cut-out, ~3000px tall, head near the top, bust cut at the bottom edge):
 * - desktop (9415:1476, 666×777): the photo is 102.7% of the frame's height, starts 2.75% above it
 *   and is centred, which is Figma's crop of Christoph's portrait;
 * - mobile (9415:881 / 9415:1500, 398×412): the photo cover-fills a 380×501 box at (9, -20.1) and
 *   the frame fades out over its bottom 27% (Figma's gradient mask).
 */
const MOBILE_FADE = "linear-gradient(180deg, #000 73.182%, transparent 100%)";

export default function EliteMentors({
  chip,
  title,
  leadMuted,
  leadStrong,
  sealedNote,
  mentors = [],
  copy,
}: {
  chip?: string;
  title?: string;
  leadMuted?: string;
  leadStrong?: string;
  sealedNote?: string;
  mentors?: IEliteMentor[];
  copy: EliteCopy;
}) {
  const tipId = useId();
  // Only confirmed mentors can be opened; sealed ones stay locked avatars.
  const confirmed = useMemo(
    () => mentors.map((m, i) => ({ m, i })).filter(({ m }) => stegaClean(m.status) === "confirmed"),
    [mentors],
  );
  const [active, setActive] = useState(confirmed[0]?.i ?? 0);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const step = useCallback(
    (dir: 1 | -1) => {
      if (!confirmed.length) return;
      const at = Math.max(
        0,
        confirmed.findIndex(({ i }) => i === active),
      );
      setActive(confirmed[(at + dir + confirmed.length) % confirmed.length].i);
    },
    [active, confirmed],
  );

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const reduceMotion = useReducedMotion();
  // Autoplay is driven by the ring timer itself (onAnimationEnd), so hover/off-screen pauses both at once
  const autoplay = confirmed.length > 1 && !reduceMotion;
  const running = autoplay && visible && !paused;

  // Every confirmed portrait stays mounted and stacked, so switching is a pure crossfade.
  const portraits = (variant: "desktop" | "mobile") =>
    confirmed.map(({ m, i }) =>
      m.photo?.src ? (
        <NextImage
          key={m._key ?? i}
          src={m.photo.src}
          width={m.photo.width}
          height={m.photo.height}
          alt={i === active ? (m.name ?? "") : ""}
          aria-hidden={i !== active}
          // desktop: the crop is ~35.6vw wide up to 1920 (~31vw above, where the rem scale changes);
          // mobile: the cover box is about full width
          sizes={variant === "desktop" ? "(min-width: 1921px) 32vw, 36vw" : "100vw"}
          quality={100}
          className={cn(
            "transition-[opacity,filter,transform] will-change-[opacity,filter,transform] motion-reduce:transition-none",
            variant === "desktop"
              ? "absolute top-[-2.7467%] left-1/2 h-[102.7027%] w-auto max-w-none -translate-x-1/2"
              : "absolute inset-0 h-full w-full object-cover",
            MORPH_EASE,
            i === active ? MORPH_IN : MORPH_OUT,
          )}
        />
      ) : null,
    );

  return (
    <div
      ref={rootRef}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") step(1);
        if (e.key === "ArrowLeft") step(-1);
      }}
      className="flex w-full flex-col px-[2rem] pt-[5rem] lg:w-[161.625rem] lg:flex-row lg:items-start lg:gap-[1.25rem] lg:px-0 lg:pt-[10rem]">
      <div className="flex w-full flex-col gap-[4rem] lg:w-[77.25rem] lg:gap-[15rem]">
        {/* heading + description */}
        <div className="flex flex-col items-center gap-[2rem] pb-[6rem] text-center lg:items-start lg:gap-[3rem] lg:pt-[7.25rem] lg:text-left">
          <EliteChip
            label={chip}
            variant="solid"
          />
          <div className="flex flex-col items-center gap-[3rem] lg:items-start">
            {title && (
              <h2
                className={cn(
                  DISPLAY,
                  CAP_TRIM,
                  GRADIENT_TRIM_PAD,
                  "w-full text-[4.5rem] leading-[0.94] tracking-[-0.135rem] whitespace-pre-line lg:text-[7.5rem] lg:tracking-[-0.225rem]",
                )}
                style={gradientText(92.92, "#fff", "46.853%", "rgba(39,39,39,0.7)", "106.52%")}>
                {title}
              </h2>
            )}
            {(leadMuted || leadStrong) && (
              <p
                className={cn(
                  "font-geist w-[44.5rem] max-w-full text-[2.25rem] leading-[1.48] text-white/60 lg:w-full lg:leading-[1.6]",
                  CAP_TRIM,
                )}>
                {leadMuted}
                {leadMuted && leadStrong ? " " : null}
                {leadStrong && <span className="text-white">{leadStrong}</span>}
              </p>
            )}
          </div>
        </div>

        {/* mobile portrait: Figma's 398×412 mask frame, 339px under the chip, fading into the black */}
        <div
          className="relative -mt-[6.875rem] aspect-[398/412] w-full overflow-hidden lg:hidden"
          style={{ WebkitMaskImage: MOBILE_FADE, maskImage: MOBILE_FADE }}>
          <div className="absolute top-[-4.8821%] left-[2.2613%] aspect-[380/501] w-[95.4774%] overflow-hidden">
            {portraits("mobile")}
          </div>
        </div>

        {/* avatars, arrows and caption */}
        <div className="flex w-full flex-col gap-[2.75rem] lg:w-[70rem]">
          <div className="flex items-center justify-between">
            {/* side padding leaves room for the ring, so the row keeps Figma's 445px */}
            <div className="flex items-center gap-[1.21rem] px-[0.4375rem] lg:gap-[1.3829rem] lg:px-[0.5rem]">
              {mentors.map((m, i) => {
                const sealed = stegaClean(m.status) !== "confirmed";
                const isActive = i === active;
                return (
                  <button
                    key={m._key ?? i}
                    type="button"
                    // sealed avatars stay focusable (not `disabled`) so the note shows on focus and tap
                    aria-disabled={sealed || undefined}
                    aria-label={m.name ?? copy.mentor.replace("{n}", String(i + 1))}
                    aria-describedby={sealed ? `${tipId}-${i}` : undefined}
                    aria-pressed={isActive}
                    onClick={() => !sealed && setActive(i)}
                    className={cn(
                      "group relative h-[6.9578rem] w-[6.9578rem] shrink-0 rounded-full outline-none lg:h-[7.9518rem] lg:w-[7.9518rem]",
                      sealed
                        ? "cursor-not-allowed"
                        : "cursor-pointer focus-visible:ring-2 focus-visible:ring-[#a3deb2]/60",
                    )}>
                    {isActive && (
                      // the ring glides to the active avatar, then fills clockwise as its timer
                      <motion.span
                        layoutId="elite-mentor-ring"
                        aria-hidden="true"
                        transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 380, damping: 34 }}
                        className="absolute inset-[-0.4375rem] lg:inset-[-0.5rem]">
                        <svg
                          viewBox="0 0 100 100"
                          className="absolute inset-0 h-full w-full -rotate-90 overflow-visible">
                          {/* 1px ring = 1.3966 viewBox units on both breakpoints (0.125/8.9518 ≈ 0.1094/7.8328) */}
                          <circle
                            cx="50"
                            cy="50"
                            r={RING_R}
                            fill="none"
                            strokeWidth={RING_STROKE}
                            className="stroke-[#a3deb2]/25"
                          />
                          <circle
                            key={active}
                            cx="50"
                            cy="50"
                            r={RING_R}
                            fill="none"
                            strokeWidth={RING_STROKE}
                            pathLength={1}
                            strokeDasharray={1}
                            strokeLinecap="round"
                            onAnimationEnd={() => autoplay && step(1)}
                            className={cn(
                              "stroke-[#a3deb2]",
                              autoplay ? "animate-elite-ring" : "[stroke-dashoffset:0]",
                            )}
                            style={
                              autoplay
                                ? {
                                    animationDuration: `${INTERVAL_MS}ms`,
                                    animationPlayState: running ? "running" : "paused",
                                  }
                                : undefined
                            }
                          />
                        </svg>
                      </motion.span>
                    )}
                    <span
                      className={cn(
                        "relative block h-full w-full  rounded-full",
                        sealed &&
                          "opacity-[0.56] transition-opacity group-hover:opacity-[0.45] group-focus-visible:opacity-[0.45] lg:opacity-60",
                      )}>
                      {m.avatar?.src && (
                        <NextImage
                          src={m.avatar.src}
                          alt=""
                          fill
                          sizes="64px"
                          quality={100}
                          className="object-cover"
                        />
                      )}
                    </span>
                    {sealed && (
                      <>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src="/elite/locked.svg"
                          alt=""
                          className="absolute top-1/2 left-1/2 hidden h-[2rem] w-[2rem] -translate-x-1/2 -translate-y-1/2 transition-opacity group-hover:opacity-[0.45] group-focus-visible:opacity-[0.45] lg:block"
                        />
                        <span
                          id={`${tipId}-${i}`}
                          role="tooltip"
                          className={cn(
                            "pointer-events-none absolute bottom-[calc(100%+1.75rem)] z-20 rounded-[1rem] bg-white px-[1.625rem] py-[1.125rem] font-geist text-[1.75rem] leading-[1.2] font-medium whitespace-nowrap text-[#0b0b0b] shadow-[0_1.5rem_3.75rem_-1.5rem_rgba(0,0,0,0.5)]",
                            "translate-y-[0.75rem] opacity-0 transition-[opacity,transform] [transition-duration:250ms] [transition-timing-function:cubic-bezier(0.22,0.61,0.36,1)]",
                            "group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100",
                            // near the right edge on mobile the pill hangs left so the section doesn't clip it
                            i >= mentors.length - 2
                              ? "right-0 lg:right-auto lg:left-1/2 lg:-translate-x-1/2"
                              : "left-1/2 -translate-x-1/2",
                          )}>
                          {sealedNote ?? copy.sealedNote}
                        </span>
                      </>
                    )}
                  </button>
                );
              })}
            </div>
            {confirmed.length > 1 && (
              <div className="hidden gap-[1rem] lg:flex">
                <button
                  type="button"
                  aria-label={copy.previousMentor}
                  onClick={() => step(-1)}
                  className="flex h-[6rem] w-[6rem] items-center justify-center rounded-[1rem] border border-solid border-white/[0.16] bg-white/[0.04] transition-colors hover:bg-white/10">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="/elite/icon-prev.svg"
                    alt=""
                    className="h-[1.75rem] w-[1.75rem]"
                  />
                </button>
                <button
                  type="button"
                  aria-label={copy.nextMentor}
                  onClick={() => step(1)}
                  className="flex h-[6rem] w-[6rem] items-center justify-center rounded-[1rem] border border-solid border-white/[0.16] bg-white transition-opacity hover:opacity-90">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="/elite/icon-next.svg"
                    alt=""
                    className="h-[1.75rem] w-[1.75rem]"
                  />
                </button>
              </div>
            )}
          </div>

          {/* every confirmed caption sits in the same grid cell: the cell is as tall as the
              tallest one, so switching mentors never moves what comes after it */}
          <div
            aria-live="polite"
            className="grid lg:w-[69.8828rem]">
            {confirmed.map(({ m, i }) => {
              const isActive = i === active;
              return (
                <div
                  key={m._key ?? i}
                  aria-hidden={!isActive}
                  className={cn(
                    "relative col-start-1 row-start-1 flex items-start justify-between gap-[2rem] transition-[opacity,filter,transform] motion-reduce:transition-none",
                    MORPH_EASE,
                    isActive
                      ? "translate-y-0 opacity-100 blur-0 [transition-duration:800ms] [transition-delay:250ms]"
                      : "pointer-events-none translate-y-[0.75rem] opacity-0 blur-[0.5rem] [transition-duration:450ms]",
                  )}>
                  <div className="flex flex-col gap-[1rem] lg:gap-[0.75rem]">
                    <p
                      className={cn(
                        "font-geist text-[2.25rem] font-medium text-white",
                        "max-lg:[text-box-trim:trim-both] max-lg:[text-box-edge:cap_alphabetic]",
                      )}>
                      {m.name}
                    </p>
                    {/* mobile joins role and achievement in one line, desktop stacks them */}
                    <div
                      dangerouslySetInnerHTML={{ __html: [m.role, m.achievement].filter(Boolean).join(" & ") }}
                      className="w-[39.625rem] font-geist text-[2rem] text-white/60 lg:hidden"
                    />
                    {m.role && <p className="hidden font-geist text-[1.875rem] text-white/60 lg:block">{m.role}</p>}
                    {m.achievement && (
                      <div
                        dangerouslySetInnerHTML={{ __html: m.achievement }}
                        className="hidden w-[32.75rem] font-geist text-[1.875rem] text-white/60 lg:block"
                      />
                    )}
                  </div>
                  {m.statusLabel && (
                    <p className="absolute top-0 right-0 font-geist text-[1.75rem] whitespace-nowrap text-[#9ee3b0] uppercase lg:static lg:tracking-[0.125rem]">
                      {m.statusLabel}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* desktop portrait: Figma's 666×777 frame clips the crop */}
      <div className="relative hidden h-[97.125rem] w-[83.25rem] shrink-0 overflow-hidden lg:block">{portraits("desktop")}</div>
    </div>
  );
}
